import type { FC } from 'react';
import {
    useCallback,
    useMemo,
    createContext,
    useContext,
    useState,
} from 'react';

import type { INavItem } from '../components/Navigation';

interface ISetUrlProps {
    profileUrl?: string;
    homepageUrl?: string;
    unauthorizedUrl?: string;
    searchUrl?: string;
}

interface INavigationContext {
    profileUrl: string | null;
    homepageUrl: string | null;
    unauthorizedUrl: string | null;
    searchUrl: string | null;
    navItems: INavItem[];
    setUrls: (props: ISetUrlProps) => void;
    setNavItems: (navItems: INavItem[]) => void;
}

const NavigationContext = createContext<INavigationContext | null>(null);

const useNavigationContext = () => {
    const contextValue = useContext(NavigationContext);

    if (!contextValue) {
        throw new Error(
            'useNavigationContext must be used within NavigationContextProvider scope'
        );
    }

    return contextValue;
};

interface INavigationProviderProps {
    profileUrl?: string | null;
    homepageUrl?: string | null;
    unauthorizedUrl?: string | null;
    searchUrl?: string | null;
    navItems?: INavItem[];
}

const NavigationContextProvider: FC<INavigationProviderProps> = ({
    children,
    ...props
}) => {
    const [profileUrl, setProfileUrl] = useState<string | null>(
        props?.profileUrl ?? null
    );
    const [unauthorizedUrl, setUnauthorizedUrl] = useState<string | null>(
        props?.unauthorizedUrl ?? null
    );
    const [homepageUrl, setHomepageUrl] = useState<string | null>(
        props?.homepageUrl ?? null
    );
    const [searchUrl, setSearchUrl] = useState<string | null>(
        props?.searchUrl ?? null
    );
    const [navItems, setNavItems] = useState<INavItem[]>(props?.navItems ?? []);

    const setUrls = useCallback(
        ({
            profileUrl,
            homepageUrl,
            unauthorizedUrl,
            searchUrl,
        }: ISetUrlProps) => {
            profileUrl && setProfileUrl(profileUrl);
            homepageUrl && setHomepageUrl(homepageUrl);
            unauthorizedUrl && setUnauthorizedUrl(unauthorizedUrl);
            searchUrl && setSearchUrl(searchUrl);
        },
        [setProfileUrl, setHomepageUrl, setUnauthorizedUrl, setSearchUrl]
    );

    const contextValue: INavigationContext = useMemo(() => {
        return {
            profileUrl,
            unauthorizedUrl,
            homepageUrl,
            navItems,
            searchUrl,
            setUrls,
            setNavItems,
        };
    }, [
        profileUrl,
        homepageUrl,
        unauthorizedUrl,
        navItems,
        searchUrl,
        setUrls,
        setNavItems,
    ]);

    return (
        <NavigationContext.Provider value={contextValue}>
            {children}
        </NavigationContext.Provider>
    );
};

NavigationContextProvider.defaultProps = {
    homepageUrl: null,
    navItems: [],
    profileUrl: null,
    unauthorizedUrl: null,
};

export { NavigationContext, useNavigationContext, NavigationContextProvider };
